// Stripe money values are integer minor units, not the catalogue's display-price units.
export type ProviderPaidInvoice = {
    amountPaid: number;
    subtotal: number;
    discountAmount: number;
    taxAmount: number | null;
    total: number;
    currency: string;
    paidAt: string | null;
    billingReason: string | null;
    livemode: boolean;
};

export type ProviderPaymentSummary = {
    providerId: string;
    payment: ProviderPaidInvoice | null;
};

// Reject mismatched provider data and malformed money before presenting a private invoice.
function parseProviderPaymentSummary(value: unknown, providerId: string): ProviderPaymentSummary {
    const response = value as Partial<ProviderPaymentSummary> | null;
    if (!response || typeof response !== 'object' || response.providerId !== providerId) {
        throw new Error('The payment summary was returned for the wrong organisation.');
    }
    if (response.payment === null) return { providerId, payment: null };

    const payment = response.payment;
    if (!payment || typeof payment !== 'object' ||
        !Number.isSafeInteger(payment.amountPaid) || payment.amountPaid < 0 ||
        !Number.isSafeInteger(payment.subtotal) ||
        !Number.isSafeInteger(payment.discountAmount) || payment.discountAmount < 0 ||
        (payment.taxAmount !== null && !Number.isSafeInteger(payment.taxAmount)) ||
        !Number.isSafeInteger(payment.total) ||
        typeof payment.currency !== 'string' || !/^[a-z]{3}$/i.test(payment.currency) ||
        (payment.paidAt !== null && (typeof payment.paidAt !== 'string' ||
            !Number.isFinite(Date.parse(payment.paidAt)))) ||
        (payment.billingReason !== null && typeof payment.billingReason !== 'string') ||
        typeof payment.livemode !== 'boolean') {
        throw new Error('The payment summary is invalid.');
    }

    // Explicitly select display fields so extra server metadata cannot enter component state.
    return {
        providerId,
        payment: {
            amountPaid: payment.amountPaid,
            subtotal: payment.subtotal,
            discountAmount: payment.discountAmount,
            taxAmount: payment.taxAmount,
            total: payment.total,
            currency: payment.currency.toUpperCase(),
            paidAt: payment.paidAt,
            billingReason: payment.billingReason,
            livemode: payment.livemode,
        },
    };
}

// The callable independently enforces Root/Super access and resolves Stripe IDs server-side.
export async function getProviderPaymentSummary(healthcareProviderId: string): Promise<ProviderPaymentSummary> {
    if (!healthcareProviderId.trim()) {
        throw new Error('Select an organisation to load its payment summary.');
    }
    // Load callable transport only when an authorized view requests this private summary.
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const callable = httpsCallable<{ healthcareProviderId: string }, unknown>(
        getFunctions(), 'getProviderPaymentSummary'
    );
    const response = await callable({ healthcareProviderId });
    return parseProviderPaymentSummary(response.data, healthcareProviderId);
}

// Respect the currency's decimal scale, including Stripe's legacy ISK/UGX charge units.
export function formatStripeInvoiceAmount(amount: number, currency: string): string {
    const normalizedCurrency = currency.toUpperCase();
    const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: normalizedCurrency });
    const decimalPlaces = normalizedCurrency === 'ISK' || normalizedCurrency === 'UGX' ?
        2 : formatter.resolvedOptions().maximumFractionDigits;
    return formatter.format(amount / Math.pow(10, decimalPlaces));
}
